import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import { NavDaCentral, SECOES_DA_CENTRAL, type SecaoDaCentral } from "../componentes/PeritoUi";
import { Recado } from "../componentes/ui";
import { calcularAlertas, quantosPedemAtencao } from "../lib/perito/alertas";
import { haGravacaoPendente, usePerito } from "../lib/perito/store";
import PeritoPericias from "./PeritoPericias";
import PeritoNova from "./PeritoNova";
import PeritoFicha from "./PeritoFicha";
import PeritoPainel from "./PeritoPainel";
import PeritoPrazos from "./PeritoPrazos";
import PeritoTarefas from "./PeritoTarefas";
import PeritoFinanceiro from "./PeritoFinanceiro";
import PeritoAlertas from "./PeritoAlertas";
import PeritoMeusDados from "./PeritoMeusDados";
import PeritoRelatorios from "./PeritoRelatorios";
import PeritoInteligencia from "./PeritoInteligencia";

// A Central do Perito (000385) — a gestão das perícias judiciais do aluno da Formação em Perícia. Era um app
// próprio (`perito.contabilidadefacilitada.com`, no ar desde 28/09, visível só para a equipe pelo card do
// Bubble); o de lá continua no ar até a virada, que é decisão do Enzo (`docs/runbooks/central-do-perito.md`).
//
// As telas, despachadas pelo trecho depois do slug, cada seção com a sua entrada na barra (`NavDaCentral`):
//
//   /ferramenta/central-do-perito                  o painel: os números, os próximos prazos, os gráficos (fase B)
//   …/pericias                                     as perícias: cards, tabela e kanban pelas 10 etapas
//   …/pericias/nova                                o cadastro: pelo despacho, que a leitura automática lê (fase C), ou à mão
//   …/pericias/<uuid>                              a ficha: etapas, tarefas, prazos, honorários, documentos (fase C), histórico
//   …/prazos                                       a agenda: o calendário do mês e as janelas de 7 e 30 dias (fase B)
//   …/tarefas                                      as tarefas de todas as perícias (fase B)
//   …/financeiro                                   os honorários, o fluxo judicial e o Carnê-Leão (fase B)
//   …/relatorios                                   o resumo executivo para imprimir e as planilhas (fase C)
//   …/inteligencia                                 as perguntas sobre a operação, por período (fase C)
//   …/alertas                                      os prazos e as tarefas que pedem atenção (fase B)
//   …/meus-dados                                   o que a conta tem e a exportação em JSON (fase B)
//
// Até a fase A, o endereço da ferramenta abria a lista das perícias; desde a fase B ele abre o painel, como lá.
// Uma tela só aparece na barra quando existe: uma entrada que não abre nada seria a promessa vazia que esta casa
// evita. Os Relatórios e a Inteligência entraram na barra com a fase C, na ordem de lá.

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RAIZ = "/ferramenta/central-do-perito";

export default function CentralDoPerito() {
  const resto = (useParams()["*"] ?? "").replace(/\/+$/, "");
  const s = usePerito();
  // Os alertas, calculados uma vez por mudança do estado: a barra mostra o número, e o painel e a tela dos alertas
  // mostram a lista.
  const alertas = useMemo(() => calcularAlertas(s.pericias, s.tarefas, s.prazos), [s.pericias, s.tarefas, s.prazos]);

  useEffect(() => {
    void usePerito.getState().carregar();
    // Na saída, a memória se esvazia: o próximo a usar o navegador não vê nada.
    return () => usePerito.getState().limpar();
  }, []);

  useEffect(() => {
    // Fechar a aba com gravação na fila perderia a alteração: o navegador pergunta.
    const aoSair = (e: BeforeUnloadEvent) => {
      if (!haGravacaoPendente()) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", aoSair);
    return () => window.removeEventListener("beforeunload", aoSair);
  }, []);

  if (!s.carregado) return <Carregando texto="Abrindo a Central do Perito…" />;
  if (s.erro) return <Recado tipo="erro" texto={s.erro} />;

  const segmentos = resto.split("/").filter(Boolean);
  const [primeiro = "", segundo] = segmentos;
  let tela;
  if (segmentos.length === 0) tela = <PeritoPainel alertas={alertas} />;
  else if (primeiro === "pericias" && segmentos.length === 1) tela = <PeritoPericias />;
  else if (primeiro === "pericias" && segundo === "nova" && segmentos.length === 2) tela = <PeritoNova />;
  else if (primeiro === "pericias" && UUID.test(segundo ?? "") && segmentos.length === 2) tela = <PeritoFicha id={segundo!} />;
  else if (segmentos.length === 1 && primeiro === "prazos") tela = <PeritoPrazos />;
  else if (segmentos.length === 1 && primeiro === "tarefas") tela = <PeritoTarefas />;
  else if (segmentos.length === 1 && primeiro === "financeiro") tela = <PeritoFinanceiro />;
  else if (segmentos.length === 1 && primeiro === "relatorios") tela = <PeritoRelatorios />;
  else if (segmentos.length === 1 && primeiro === "inteligencia") tela = <PeritoInteligencia />;
  else if (segmentos.length === 1 && primeiro === "alertas") tela = <PeritoAlertas alertas={alertas} />;
  else if (segmentos.length === 1 && primeiro === "meus-dados") tela = <PeritoMeusDados />;
  else
    tela = (
      <div className="surface p-6 text-sm text-cinza">
        Esta página da Central do Perito não existe.{" "}
        <Link to={RAIZ} className="font-semibold text-link hover:underline">Abrir o painel</Link>
      </div>
    );
  // A seção acesa na barra: a do primeiro trecho (a ficha e o cadastro são das Perícias).
  const ativa = (SECOES_DA_CENTRAL.find((x) => x.id === primeiro)?.id ?? "") as SecaoDaCentral;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-6 sm:px-6" data-central-do-perito>
      {!s.podeEscrever && (
        <Recado tipo="erro" texto="Sua conta pode ver o que já cadastrou, mas não pode gravar: a Central do Perito é da Formação em Perícia, e o curso não está liberado agora." />
      )}
      {s.aviso && (
        <div role="alert" className="flex items-start justify-between gap-3 rounded-xl bg-erro-fundo p-3.5 text-sm text-erro-tinta" data-aviso-da-fila>
          <span>{s.aviso}</span>
          <button type="button" onClick={s.limparAviso} className="font-semibold underline">Fechar</button>
        </div>
      )}
      <NavDaCentral raiz={RAIZ} ativa={ativa} pedemAtencao={quantosPedemAtencao(alertas)} />
      {tela}
    </div>
  );
}
